import type { Editor } from '@tiptap/react';
import { useCallback, useMemo, useRef } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import OdinController from '../../dynamic-form/odin-controller.component';
import { RichTextArea } from '../../rich-text/rich-text-area';

export interface HfRichTextAreaProps<TFieldValues extends FieldValues = FieldValues>
  extends DynamicFieldComponentProps<string> {
  control: Control<TFieldValues>;
  name: string;
  placeholder?: string;
  minHeight?: number;
  maxHeight?: number;
}

export function HfRichTextArea<TFieldValues extends FieldValues = FieldValues>({
  value,
  control,
  name,
  label,
  readOnly,
  required,
  minHeight,
  maxHeight,
  onChange,
  fieldValueConverter,
  labelStyles,
  placeholder,
}: HfRichTextAreaProps<TFieldValues>) {
  // RHF's setFocus(name) calls `.focus()` on whatever the field ref points at. The Tiptap
  // editor isn't a DOM element, so expose a tiny focusable handle that forwards to
  // editor.commands.focus(). The ref + memo are both stable, so plugging this into RHF only
  // happens once per mount.
  const editorRef = useRef<Editor | null>(null);
  const captureEditor = useCallback((editor: Editor | null) => {
    editorRef.current = editor;
  }, []);
  const focusableHandle = useMemo(
    () => ({
      focus: () => {
        editorRef.current?.commands.focus();
      },
    }),
    []
  );

  return (
    <OdinController
      name={name}
      control={control}
      onChange={onChange}
      fieldValueConverter={fieldValueConverter}
      defaultValue={value}
      render={({ field, fieldState: { error } }) => {
        field.ref(focusableHandle);
        return (
          <RichTextArea
            content={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={placeholder}
            minHeight={minHeight}
            maxHeight={maxHeight}
            label={label}
            labelStyles={labelStyles}
            required={required}
            readOnly={readOnly}
            error={error?.message}
            editorRef={captureEditor}
          />
        );
      }}
    />
  );
}
